import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { usePayment } from '../context/PaymentContext';
import { useNavigate } from 'react-router-dom';
import '../styles/FreeMoney.css';
import AuthModal from "./AuthModal";
import PaymentModal from "./PaymentModal";
import ReferralProgram from "./ReferralProgram";

import { getApiUrl } from "../utils/config";
const API_URL = getApiUrl();

const FreeMoney = () => {
    const { t } = useTranslation();
    const { isAuth, showNotification } = useAuth();
    const {
        selectedCurrency,
        getCurrentBonusBalance,
        fetchBalance,
        fiatRates,
        isBalanceLoaded,
        isCurrencyLoaded
    } = usePayment();

    const navigate = useNavigate();

    // Состояния для welcome промокодов
    const [welcomePromocodes, setWelcomePromocodes] = useState([]);

    // Состояние для модалки авторизации
    const [showAuthModal, setShowAuthModal] = useState(false);

    // Состояние для модального окна с информацией о бонусе +500%
    const [showBonusInfoModal, setShowBonusInfoModal] = useState(false);

    // Состояние для модального окна пополнения
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    // Состояние для модального окна "Кэшбек и бонусы"
    const [showCashbackInfoModal, setShowCashbackInfoModal] = useState(false);

    // Состояние для модального окна "Rakeback"
    const [showRakebackInfoModal, setShowRakebackInfoModal] = useState(false);

    // Состояние для модального окна "Реферальная программа"
    const [showReferralModal, setShowReferralModal] = useState(false);

    // Состояние для прокрутки freebar
    const [scrollPosition, setScrollPosition] = useState(0);

    // Для неавторизованных пользователей используем RUB
    const currentCurrency = isAuth ? selectedCurrency : 'RUB';
    const currentFiatRates = isAuth ? fiatRates : {};



    const bonusCashbackTable = useMemo(() => [
        { min: convertRub(1000000), percent: 20 },
        { min: convertRub(700000), percent: 10 },
        { min: convertRub(500000), percent: 5 },
        { min: convertRub(100000), percent: 4 },
        { min: convertRub(50000), percent: 3 },
        { min: convertRub(20000), percent: 2 },
        { min: convertRub(5000), percent: 1 },
    ], [currentCurrency, currentFiatRates]);

    // Загрузка данных при авторизации или для неавторизованных
    useEffect(() => {
        if (isAuth && isBalanceLoaded && isCurrencyLoaded) {
            fetchWelcomePromocodes();
        } else if (!isAuth) {
            fetchWelcomePromocodesPublic();
        }
    }, [isAuth, isBalanceLoaded, isCurrencyLoaded]);

    // Получение символа валюты
    function getCurrencySymbol(currency) {
        switch (currency) {
            case 'USD': return '$';
            case 'RUB': return '₽';
            case 'BRL': return 'R$';
            case 'USDT': return '₮';
            case 'EUR': return '€';
            default: return '';
        }
    }

    // Функция для пересчёта RUB в выбранную валюту
    function convertRub(value) {
        if (!currentCurrency || currentCurrency === 'RUB') return value;
        if (currentCurrency === 'USD' && currentFiatRates?.RUB_USD) return Math.round(value * currentFiatRates.RUB_USD);
        if (currentCurrency === 'BRL' && currentFiatRates?.RUB_BRL) return Math.round(value * currentFiatRates.RUB_BRL);
        if (currentCurrency === 'USDT' && currentFiatRates?.RUB_USDT) return Math.round(value * currentFiatRates.RUB_USDT);
        if (currentCurrency === 'EUR' && currentFiatRates?.RUB_EUR) return Math.round(value * currentFiatRates.RUB_EUR);
        return value;
    }

    const fetchWelcomePromocodes = async () => {
        try {
            const response = await fetch(`${API_URL}/api/promo/user-activated`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await response.json();
            if (data.success) {
                // Фильтруем только WELCOME промокоды
                const welcomePromos = data.activatedPromoCodes.filter(promo =>
                    promo.code.startsWith('WELCOME')
                );

                // Создаем массив из 4 слотов для welcome промокодов
                const welcomeSlots = [
                    { code: 'WELCOME', status: 'inactive', id: null },
                    { code: 'WELCOME2', status: 'inactive', id: null },
                    { code: 'WELCOME3', status: 'inactive', id: null },
                    { code: 'WELCOME4', status: 'inactive', id: null }
                ];

                // Обновляем статусы активированных промокодов
                welcomePromos.forEach(promo => {
                    const slotIndex = welcomeSlots.findIndex(slot => slot.code === promo.code);
                    if (slotIndex !== -1) {
                        welcomeSlots[slotIndex] = {
                            ...welcomeSlots[slotIndex],
                            status: promo.is_used ? 'used' : 'active',
                            id: promo.id,
                            bonus_percent: promo.bonus_percent,
                            free_spins_count: promo.free_spins_count,
                            activated_at: promo.activated_at
                        };
                    }
                });

                setWelcomePromocodes(welcomeSlots);
            }
        } catch (error) {
            console.error('Ошибка при загрузке welcome промокодов:', error);
        }
    };

    const fetchWelcomePromocodesPublic = async () => {
        try {
            const response = await fetch(`${API_URL}/api/promo/welcome-info`, {
                method: 'GET',
            });
            const data = await response.json();
            if (data.success && data.welcomePromocodes) {
                // Создаем массив из 4 слотов с данными с сервера
                const welcomeSlots = [
                    { code: 'WELCOME', status: 'inactive', id: null },
                    { code: 'WELCOME2', status: 'inactive', id: null },
                    { code: 'WELCOME3', status: 'inactive', id: null },
                    { code: 'WELCOME4', status: 'inactive', id: null }
                ];

                // Обновляем данные из ответа сервера
                data.welcomePromocodes.forEach((promo, idx) => {
                    const slotIndex = welcomeSlots.findIndex(slot => slot.code === promo.code);
                    if (slotIndex !== -1) {
                        welcomeSlots[slotIndex] = {
                            ...welcomeSlots[slotIndex],
                            status: 'inactive',
                            bonus_percent: promo.bonus_percent,
                            free_spins_count: promo.free_spins_count,
                            min_deposit: promo.min_deposit,
                            bonus_max_amount: promo.bonus_max_amount,
                            description: [
                                'Первый депозит',
                                'Второй депозит',
                                'Третий депозит',
                                'Четвертый депозит',
                            ][slotIndex]
                        };
                    }
                });

                setWelcomePromocodes(welcomeSlots);
            }
        } catch (error) {
            console.error('Ошибка при загрузке публичной информации о welcome промокодах:', error);
        }
    };

    const currencySymbol = getCurrencySymbol(currentCurrency);

    // Функция для прокрутки к баннеру по центру экрана
    const scrollToBanner = (bannerId) => {
        console.log('Попытка прокрутки к:', bannerId);

        // Небольшая задержка для гарантии, что элемент отрендерился
        setTimeout(() => {
            const element = document.getElementById(bannerId);
            console.log('Найден элемент:', element);

            if (element) {
                // Попробуем сначала простой scrollIntoView
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });

                console.log('Прокрутка выполнена через scrollIntoView');
            } else {
                console.error('Элемент не найден:', bannerId);

                // Попробуем найти все элементы с похожими ID для отладки
                const allElements = document.querySelectorAll('[id*="banner"]');
                console.log('Все элементы с banner в ID:', allElements);
            }
        }, 100);
    };

    // Функции для прокрутки freebar
    const scrollFreebarLeft = () => {
        const container = document.querySelector('.freebar-container');
        if (container) {
            const scrollAmount = 200;
            container.scrollBy({
                left: -scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const scrollFreebarRight = () => {
        const container = document.querySelector('.freebar-container');
        if (container) {
            const scrollAmount = 200;
            container.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className="freemoney-page-container">



            {/* Заголовок страницы */}
            <div className="freemoney-header">
                <p className="freemoney-title">Халява</p>
                <p className="freemoney-subtitle">Получайте бонусы и участвуйте в акциях для увеличения баланса</p>
            </div>


            {/* Навбар FreeMoney */}
            <div className="freebar">
                <button
                    className="freebar-scroll-btn left"
                    onClick={scrollFreebarLeft}
                    aria-label="Прокрутить влево"
                >
                    &#8249;
                </button>
                <div className="freebar-container">
                    <button
                        className="freebar-button"
                        onClick={() => {
                            console.log('Клик по кнопке Бонус +500%');
                            scrollToBanner('banner-bonus-500');
                        }}
                    >
                        Бонус +500%
                    </button>
                    <button
                        className="freebar-button"
                        onClick={() => {
                            console.log('Клик по кнопке Кэшбэк');
                            scrollToBanner('banner-cashback');
                        }}
                    >
                        Кэшбэк
                    </button>
                    <button
                        className="freebar-button"
                        onClick={() => {
                            console.log('Клик по кнопке Реферальная программа');
                            scrollToBanner('banner-referral');
                        }}
                    >
                        Реферальная программа
                    </button>
                    <button
                        className="freebar-button"
                        onClick={() => {
                            console.log('Клик по кнопке Telegram');
                            scrollToBanner('banner-telegram');
                        }}
                    >
                        Telegram
                    </button>
                    <button
                        className="freebar-button"
                        onClick={() => {
                            console.log('Клик по кнопке Приложение');
                            scrollToBanner('banner-app');
                        }}
                    >
                        Приложение
                    </button>
                    <button
                        className="freebar-button"
                        onClick={() => {
                            console.log('Клик по кнопке Rakeback');
                            scrollToBanner('banner-rakeback');
                        }}
                    >
                        Rakeback
                    </button>
                </div>
                <button
                    className="freebar-scroll-btn right"
                    onClick={scrollFreebarRight}
                    aria-label="Прокрутить вправо"
                >
                    &#8250;
                </button>
            </div>

            {/* Баннеры акций */}
            <div className="bonuses-banner-vertical">
                <button
                    id="banner-bonus-500"
                    className="bonuses-banner bonus-banner-with-text"
                    onClick={() => setShowBonusInfoModal(true)}
                >
                    <img src="/assets/banners/bonuses.webp" alt="Бонус +500%" className="bonuses-banner-img" />
                    <div className="bonus-banner-text-overlay">
                        <div className="bonus-banner-main-text">БОНУС +500%</div>
                        <div className="bonus-banner-sub-text">НА ПЕРВЫЕ 4 ДЕПОЗИТА</div>
                        <div className="bonus-banner-action-text">ЗАБРАТЬ</div>
                    </div>
                </button>
                <button
                    id="banner-cashback"
                    className="bonuses-banner cashback-banner-with-text"
                    onClick={() => setShowCashbackInfoModal(true)}
                >
                    <img src="/assets/banners/kashback.webp" alt="Кэшбек и бонусы" className="bonuses-banner-img" />
                    <div className="cashback-banner-text-overlay">
                        <div className="cashback-banner-main-text">КЭШБЭК</div>
                        <div className="cashback-banner-main-text2">ДО 30%</div>
                        <div className="cashback-banner-sub-text">БЕЗ ОТЫГРЫША</div>
                        <div className="cashback-banner-sub-text2">СРАЗУ НА СЧЁТ</div>
                        <div className="cashback-banner-action-text">ПОЛУЧИТЬ</div>
                    </div>
                </button>
                <button
                    id="banner-referral"
                    className="bonuses-banner referral-banner-with-text"
                    onClick={() => {
                        if (isAuth) {
                            navigate('/referral');
                        } else {
                            setShowAuthModal(true);
                        }
                    }}
                >
                    <img src="/assets/banners/refsharb.webp" alt="Реферальная программа" className="bonuses-banner-img" />
                    <div className="referral-banner-text-overlay">
                        <div className="referral-banner-main-text">РЕФЕРАЛЬНАЯ</div>
                        <div className="referral-banner-main-text2">ПРОГРАММА</div>
                        <div className="referral-banner-sub-text">СТАНЬТЕ ПАРТНЕРОМ</div>
                        <div className="referral-banner-sub-text2">ПОЛУЧАЙТЕ <span className="referral-banner-percent">ДО 60%</span></div>
                        <div className="referral-banner-action-text">ПРИСОЕДИНИТЬСЯ</div>
                    </div>
                </button>
                <button
                    id="banner-rakeback"
                    className="bonuses-banner rakeback-banner-with-text"
                    onClick={() => setShowRakebackInfoModal(true)}
                >
                    <img src="/assets/banners/rakebackb.webp" alt="Rakeback счет" className="bonuses-banner-img" />
                    <div className="rakeback-banner-text-overlay">
                        <div className="rakeback-banner-main-text">ЕЖЕДНЕВНЫЙ</div>
                        <div className="rakeback-banner-main-text2">RAKEBACK</div>
                        <div className="rakeback-banner-sub-text">СРАЗУ НА СЧЁТ</div>
                        <div className="rakeback-banner-sub-text2">БЕЗ ОТЫГРЫША</div>
                        <div className="rakeback-banner-action-text">ПОЛУЧИТЬ</div>
                    </div>
                </button>
                <button
                    id="banner-telegram"
                    className="bonuses-banner telegram-banner-with-text"
                    onClick={() => window.open('https://t.me/oncaspins', '_blank')}
                >
                    <img src="/assets/banners/tg.webp" alt="Telegram канал" className="bonuses-banner-img" />
                    <div className="telegram-banner-text-overlay">
                        <div className="telegram-banner-main-text">+30 ФРИСПИНОВ</div>
                        <div className="telegram-banner-sub-text">ЗА АВТОРИЗАЦИЮ</div>
                        <div className="telegram-banner-sub-text2">В ТЕЛЕГРАММ</div>
                        <div className="telegram-banner-action-text">ПЕРЕЙТИ</div>
                    </div>
                </button>
                <button
                    id="banner-app"
                    className="bonuses-banner app-banner-with-text"
                    onClick={() => {
                        // Скачивание APK файла
                        const link = document.createElement('a');
                        link.href = '/apps/oncaspin.apk';
                        link.download = 'oncaspin.apk';
                        link.click();
                    }}
                >
                    <img src="/assets/banners/app.webp" alt="Скачать приложение" className="bonuses-banner-img" />
                    <div className="app-banner-text-overlay">
                        <div className="app-banner-main-text">+30 ФРИСПИНОВ</div>
                        <div className="app-banner-sub-text">ЗА ВХОД ИЗ МОБИЛЬНОГО</div>
                        <div className="app-banner-sub-text2">ПРИЛОЖЕНИЯ</div>
                        <div className="app-banner-action-text">Установить</div>
                    </div>
                </button>
            </div>



            {showReferralModal && (
                <div className="bonus-info-modal-overlay" onClick={() => setShowReferralModal(false)}>
                    <div className="bonus-info-modal-content" onClick={e => e.stopPropagation()} style={{ marginTop: 32 }}>
                        <button className="profile-modal-close" onClick={() => setShowReferralModal(false)}>×</button>
                        <div className="referral-modal-center">
                            <ReferralProgram />
                        </div>
                    </div>
                </div>
            )}
            {showBonusInfoModal && (
                <div className="bonus-info-modal-overlay" onClick={() => setShowBonusInfoModal(false)}>
                    <div className="bonus-info-modal-content bonus-500-modal-gradient" onClick={e => e.stopPropagation()} style={{
                        position: 'relative'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            borderRadius: '24px',
                            zIndex: 1
                        }}></div>
                        <button className="profile-modal-close" onClick={() => setShowBonusInfoModal(false)}>×</button>
                        <div className="bonus-info-modal-body" style={{ position: 'relative', zIndex: 2 }}>
                            <h2 style={{ textAlign: 'center', marginBottom: 16, color: '#fff' }}>
                                Бонус <span style={{ color: '#fff' }}>+500%</span> на депозит
                            </h2>
                            <p style={{ textAlign: 'center', fontSize: 18, marginBottom: 24, color: '#fff' }}>
                                Первые 4 пополнения основного счета дают вам дополнительный бонус к депозиту на бонусный счет
                            </p>
                            <div className="bonus-modal-get-center">
                                <button
                                    className="bonus-modal-get"
                                    onClick={() => {
                                        setShowBonusInfoModal(false);
                                        setShowPaymentModal(true);
                                    }}
                                >
                                    Получить бонус
                                </button>
                            </div>
                            <h3 style={{ textAlign: 'center', margin: '32px 0 16px', color: '#fff' }}>Как забрать бонус?</h3>
                            <div className="bonus-info-steps" style={{ gap: 0, display: 'flex', flexDirection: 'column' }}>
                                {/* Шаг 1 - центрированный с mobile.png */}
                                <div className="bonus-info-step-center" style={{ marginBottom: 0 }}>
                                    <img src="/assets/banners/mobile.webp" alt="Мобильное приложение" />
                                    <div className="bonus-info-step-label" style={{ color: '#fff' }}>ШАГ 1</div>
                                    <div className="bonus-info-step-title" style={{ color: '#fff' }}><b>Сделайте депозит — получите бонус</b></div>
                                    <div className="bonus-info-step-desc" style={{ color: '#fff' }}>
                                        Первые 4 пополнения основного счета дают вам дополнительный бонус к депозиту на бонусный счет
                                    </div>
                                </div>
                                {/* Шаг 2 - step2.png по центру */}
                                <div className="bonus-info-step-center" style={{ marginBottom: 0 }}>
                                    <img src="/assets/banners/step2.webp" alt="Шаг 2" />
                                    <div className="bonus-info-step-label" style={{ color: '#fff' }}>ШАГ 2</div>
                                    <div className="bonus-info-step-title" style={{ color: '#fff' }}><b>Заберите бонус в 500%</b></div>
                                    <div className="bonus-info-step-desc" style={{ color: '#fff' }}>
                                        Бонус зачисляется автоматически после пополнения основного счета
                                    </div>
                                </div>
                                
                                {/* Welcome Bonuses Row - отдельный блок во всю ширину */}
                                <div className="welcome-bonuses-full-width-wrapper">
                                    <div className="welcome-bonuses-row">
                                        {[1, 2, 3, 4].map((num, idx) => {
                                            const imgSrc = `/assets/banners/well${num}.webp`;
                                            let bonus;
                                            let showBtn = false;
                                            let showLockedBtn = false;
                                            let btnHandler = undefined;
                                            
                                            if (welcomePromocodes.length === 4) {
                                                // Используем данные с сервера для всех пользователей
                                                bonus = welcomePromocodes[idx];
                                                
                                                if (isAuth) {
                                                    // Для авторизованных показываем реальный статус
                                                    showBtn = bonus.status === 'active';
                                                    showLockedBtn = !showBtn;
                                                    btnHandler = showBtn ? () => {
                                                        setShowBonusInfoModal(false);
                                                        setShowPaymentModal(true);
                                                    } : undefined;
                                                } else {
                                                    // Для неавторизованных: первый активен, остальные заблокированы
                                                    showBtn = idx === 0;
                                                    showLockedBtn = idx !== 0;
                                                    btnHandler = () => {
                                                        setShowBonusInfoModal(false);
                                                        setShowAuthModal(true);
                                                    };
                                                }
                                            } else {
                                                // Fallback если данные не загрузились
                                                bonus = {
                                                    code: `WELCOME${num === 1 ? '' : num}`,
                                                    status: 'active',
                                                    bonus_percent: [200, 100, 75, 50][idx],
                                                    free_spins_count: [200, 50, 0, 0][idx],
                                                    description: [
                                                        'Первый депозит',
                                                        'Второй депозит',
                                                        'Третий депозит',
                                                        'Четвертый депозит',
                                                    ][idx],
                                                };
                                                showBtn = idx === 0;
                                                showLockedBtn = idx !== 0;
                                                btnHandler = () => {
                                                    setShowBonusInfoModal(false);
                                                    setShowAuthModal(true);
                                                };
                                            }
                                            return (
                                                <div className="welcome-bonus-card-modal active" key={bonus.code}>
                                                    <div className="welcome-bonus-info">
                                                        <div className="welcome-bonus-title">{bonus.description || `${num}-й депозит`}</div>
                                                        <div className="welcome-bonus-details">
                                                            {bonus.bonus_percent > 0 && <span className="welcome-bonus-percent">+{bonus.bonus_percent}%</span>}
                                                            {bonus.free_spins_count > 0 && <span className="welcome-bonus-fs">{bonus.free_spins_count} FS</span>}
                                                        </div>
                                                        <div className="welcome-bonus-status">
                                                            {showBtn && <button className="welcome-bonus-btn" onClick={btnHandler}>Получить</button>}
                                                            {showLockedBtn && <button className="welcome-bonus-btn welcome-bonus-btn-locked" disabled>Заблокирован</button>}
                                                        </div>
                                                    </div>
                                                    <img src={imgSrc} alt={bonus.code} className="welcome-bonus-img" />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                {/* Шаг 3 и 4 - объединённый блок, картинки рядом, текст под ними */}
                                <div className="bonus-info-step-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 0 }}>
                                    <div style={{ display: 'flex', flexDirection: 'row', gap: 32, justifyContent: 'center', alignItems: 'center', marginBottom: 12 }}>
                                        <img src="/assets/banners/step4.webp" alt="Шаг 4" style={{ objectFit: 'contain' }} />
                                        <img src="/assets/banners/step3.webp" alt="Шаг 3" style={{ objectFit: 'contain' }} />
                                    </div>
                                    <div style={{ flex: 1, textAlign: 'center' }}>
                                        <div className="bonus-info-step-label" style={{ color: '#fff' }}>ШАГ 3</div>
                                        <div className="bonus-info-step-title" style={{ color: '#fff' }}><b>Кэшбек за 24 часа</b></div>
                                        <div className="bonus-info-step-desc" style={{ color: '#fff' }}>
                                            <div style={{ background: 'rgba(72, 145, 255, 0.1)', padding: '16px', borderRadius: '12px', marginBottom: '16px', color: '#fff' }}>
                                                <b>Формула кэшбека:</b> Ставки за 24 часа × ваш личный процент = доступно к переводу
                                            </div>
                                            <div style={{ marginBottom: '16px', color: '#fff' }}>
                                                <b>Как это работает:</b>
                                                <ul style={{ textAlign: 'left', display: 'inline-block', marginTop: '8px', color: '#fff' }}>
                                                    <li>Можно забирать в любой момент — никаких ограничений по времени</li>
                                                    <li>Система учитывает уже забранное за последние 24 часа</li>
                                                    <li>Личный процент зависит от вашего игрового уровня (от 1% до 10%)</li>
                                                    <li>Лимит — не больше, чем есть на бонусном счёте</li>
                                                </ul>
                                            </div>
                                            <div style={{ background: 'rgba(151, 53, 226, 0.1)', padding: '12px', borderRadius: '8px', fontSize: '0.9rem', color: '#fff' }}>
                                                <b>Пример:</b> У вас бонусный баланс 10 000₽, за сутки поставили 50 000₽, ваш процент 4%.<br />
                                                Доступно к переводу: 50 000₽ × 4% = <b style={{ color: '#fff' }}>2 000₽</b>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bonus-info-divider" style={{ width: '100%', height: 2, background: 'linear-gradient(180deg, #4891ff 0%, #9735e2 100%)', margin: '32px 0 24px 0', borderRadius: 2 }}></div>
                            <div className="bonus-info-footer-text" style={{ color: '#fff' }}>
                                <ol style={{ color: '#fff' }}>
                                    <li>Максимальная сумма бонуса: 50 000 ₽ за один депозит и 200 000 ₽ за четыре депозита.</li>
                                    <li>Бонусное предложение доступно только для новых клиентов Oncaspin.</li>
                                    <li>Деньги, переведённые с бонусного счёта на основной счёт, становятся мгновенно доступны для вывода.</li>
                                    <li>Подробную информацию о кэшбеке и отыгрыше можно найти в разделе <b style={{ color: '#fff' }}>"Кэшбек и бонусы"</b>.</li>
                                    <li>Oncaspin оставляет за собой право в любой момент провести верификацию владельца игрового аккаунта или ограничить участие игрока в акции.</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showCashbackInfoModal && (
                <div className="bonus-info-modal-overlay" onClick={() => setShowCashbackInfoModal(false)}>
                    <div className="bonus-info-modal-content cashback-modal-gradient" onClick={e => e.stopPropagation()} style={{
                        position: 'relative'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            borderRadius: '24px',
                            zIndex: 1
                        }}></div>
                        <button className="profile-modal-close" onClick={() => setShowCashbackInfoModal(false)}>×</button>
                        <div className="bonus-info-modal-body" style={{ position: 'relative', zIndex: 2 }}>
                            <h1 className="cbc-title">
                                Кэшбэк за 7 дней
                            </h1>

                            <div className="cbc-intro">
                                <p>
                                    Возвращаем часть проигрыша за прошедшую неделю автоматически и без отыгрыша.
                                </p>
                            </div>

                            {/* Быстрые хайлайты */}
                            <div className="cbc-highlights">
                                <div className="cbc-highlight-card">
                                    <div className="cbc-highlight-emoji">⚡</div>
                                    <div className="cbc-highlight-title">Автоначисление</div>
                                    <div className="cbc-highlight-sub">Каждую пятницу в 21:00 UTC</div>
                                </div>
                                <div className="cbc-highlight-card">
                                    <div className="cbc-highlight-emoji">🏦</div>
                                    <div className="cbc-highlight-title">На основной счёт</div>
                                    <div className="cbc-highlight-sub">Сразу доступно для вывода</div>
                                </div>
                                <div className="cbc-highlight-card">
                                    <div className="cbc-highlight-emoji">📈</div>
                                    <div className="cbc-highlight-title">До 10%</div>
                                    <div className="cbc-highlight-sub">Процент зависит от уровня</div>
                                </div>
                            </div>

                            <div className="cbc-section">
                                <h2 className="cbc-section-title">Как это работает</h2>
                                <ol className="cbc-ol">
                                    <li>Считаем ваш результат за неделю: все ставки минус все выигрыши.</li>
                                    <li>Применяем ваш личный процент (зависит от уровня игрока: 1–10%).</li>
                                    <li>Начисляем кэшбэк каждую пятницу в 21:00 UTC на основной счёт.</li>
                                </ol>
                                <div className="cbc-formula">
                                    Формула: проигрыш за неделю × ваш процент = кэшбэк
                                </div>
                            </div>

                            {/* Три ключевых преимущества */}
                            <div className="cbc-benefits">
                                <div className="cbc-benefit-card">
                                    <div className="cbc-benefit-icon">🎯</div>
                                    <div className="cbc-benefit-title">Без отыгрыша</div>
                                    <div className="cbc-benefit-text">Кэшбэк сразу чистыми на основном счёте — никаких условий.</div>
                                </div>
                                <div className="cbc-benefit-card">
                                    <div className="cbc-benefit-icon">🔁</div>
                                    <div className="cbc-benefit-title">Раз в неделю</div>
                                    <div className="cbc-benefit-text">Сбор за 7 дней, начисление по расписанию — стабильно и прозрачно.</div>
                                </div>
                                <div className="cbc-benefit-card">
                                    <div className="cbc-benefit-icon">🛡️</div>
                                    <div className="cbc-benefit-title">Честный расчёт</div>
                                    <div className="cbc-benefit-text">Считается только фактический результат: ставки минус выигрыши.</div>
                                </div>
                            </div>

                            <div className="cbc-example">
                                <h3 className="cbc-section-title">Пример</h3>
                                <div className="cbc-example-content">
                                    <div className="cbc-example-row">
                                        <span>Ставки за неделю</span>
                                        <b>{convertRub(150000).toLocaleString()} {currencySymbol}</b>
                                    </div>
                                    <div className="cbc-example-row">
                                        <span>Выигрыши за неделю</span>
                                        <b>{convertRub(120000).toLocaleString()} {currencySymbol}</b>
                                    </div>
                                    <div className="cbc-example-row">
                                        <span>Проигрыш</span>
                                        <b>{convertRub(30000).toLocaleString()} {currencySymbol}</b>
                                    </div>
                                    <div className="cbc-example-row">
                                        <span>Личный процент</span>
                                        <b>5%</b>
                                    </div>
                                    <div className="cbc-example-result">
                                        Итоговый кэшбэк: {convertRub(1500).toLocaleString()} {currencySymbol}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="cbc-params">
                                <h3 className="cbc-section-title">Параметры начисления</h3>
                                <div className="cbc-params-grid">
                                    <div className="cbc-param-card">
                                        <div className="cbc-param-title">Время начисления</div>
                                        <div className="cbc-param-sub">Пятница, 21:00 UTC (сб 00:00 МСК)</div>
                                    </div>
                                    <div className="cbc-param-card">
                                        <div className="cbc-param-title">Период</div>
                                        <div className="cbc-param-sub">С прошлой до текущей пятницы</div>
                                    </div>
                                    <div className="cbc-param-card">
                                        <div className="cbc-param-title">Зачисление</div>
                                        <div className="cbc-param-sub">На основной счёт, без отыгрыша</div>
                                    </div>
                                    <div className="cbc-param-card">
                                        <div className="cbc-param-title">Лимит</div>
                                        <div className="cbc-param-sub">{convertRub(50000).toLocaleString()} {currencySymbol} в неделю</div>
                                    </div>
                                </div>
                            </div>

                            {/* Советы, чтобы получать больше */}
                            <div className="cbc-tips">
                                <h3 className="cbc-section-title">Как получать больше кэшбэка</h3>
                                <ul className="cbc-ul">
                                    <li>Играй стабильно в течение недели — равномерная активность повышает итоговую сумму.</li>
                                    <li>Поднимай уровень аккаунта — с ростом уровня растёт и личный процент.</li>
                                    <li>Не забывай про пятницу — кэшбэк приходит вечером, планируй вывод заранее.</li>
                                </ul>
                            </div>

                            {/* Быстрые факты */}
                            <div className="cbc-facts">
                                <div className="cbc-fact">⏱ Начисление по расписанию — без ручных заявок</div>
                                <div className="cbc-fact">💸 Сразу на основной счёт — доступно к выводу</div>
                                <div className="cbc-fact">🧮 Прозрачная формула — всё видно и понятно</div>
                            </div>

                            {/* FAQ */}
                            <div className="cbc-faq">
                                <h3 className="cbc-section-title">Частые вопросы</h3>
                                <div className="cbc-faq-item">
                                    <div className="cbc-faq-q">Когда приходит кэшбэк?</div>
                                    <div className="cbc-faq-a">Каждую пятницу в 21:00 UTC. Если вы из другого часового пояса, ориентируйтесь на эквивалентное местное время.</div>
                                </div>
                                <div className="cbc-faq-item">
                                    <div className="cbc-faq-q">Есть ли отыгрыш?</div>
                                    <div className="cbc-faq-a">Нет. Начисления поступают на основной счёт и доступны к выводу сразу.</div>
                                </div>
                                <div className="cbc-faq-item">
                                    <div className="cbc-faq-q">От чего зависит процент?</div>
                                    <div className="cbc-faq-a">От уровня игрока в вашей программе лояльности. Чем выше уровень — тем выше процент (1–10%).</div>
                                </div>
                            </div>

                            {/* CTA */}
                            <div className="cbc-cta-row">
                                <button
                                    className="cbc-cta primary"
                                    onClick={() => setShowCashbackInfoModal(false)}
                                >
                                    Понятно, закрыть
                                </button>
                                <button
                                    className="cbc-cta secondary"
                                    onClick={() => {
                                        setShowCashbackInfoModal(false);
                                        if (isAuth) {
                                            setShowPaymentModal(true);
                                        } else {
                                            setShowAuthModal(true);
                                        }
                                    }}
                                >
                                    Пополнить счёт
                                </button>
                            </div>

                            <div className="cbc-important">
                                <h3 className="cbc-section-title">Важно</h3>
                                <ul className="cbc-ul">
                                    <li>Личный процент зависит от вашего уровня (1–10%).</li>
                                    <li>Кэшбэк доступен для вывода сразу после начисления.</li>
                                    <li>Сумма зависит только от вашего результата за неделю.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showRakebackInfoModal && (
                <div className="bonus-info-modal-overlay" onClick={() => setShowRakebackInfoModal(false)}>
                    <div className="bonus-info-modal-content rakeback-modal-gradient" onClick={e => e.stopPropagation()} style={{
                        position: 'relative'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            borderRadius: '24px',
                            zIndex: 1
                        }}></div>
                        <button className="profile-modal-close" onClick={() => setShowRakebackInfoModal(false)}>×</button>
                        <div className="bonus-info-modal-body" style={{ position: 'relative', zIndex: 2 }}>
                            {/* Hero секция с большой цифрой */}
                            <div style={{
                                textAlign: 'center',
                                marginBottom: '48px',
                                padding: '40px 20px',
                                background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 152, 0, 0.15) 100%)',
                                borderRadius: '24px',
                                border: '2px solid rgba(255, 193, 7, 0.3)'
                            }}>
                                <div style={{
                                    fontSize: '8rem',
                                    fontWeight: '900',
                                    background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    lineHeight: '1',
                                    marginBottom: '16px'
                                }}>
                                    24h
                                </div>
                                <h1 style={{
                                    fontSize: '2.5rem',
                                    fontWeight: '800',
                                    color: '#fff',
                                    marginBottom: '16px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '2px'
                                }}>
                                    RAKEBACK
                                </h1>
                                <p style={{
                                    fontSize: '1.2rem',
                                    color: '#fff',
                                    fontWeight: '600',
                                    margin: 0
                                }}>
                                    Мгновенный возврат каждые 24 часа
                                </p>
                            </div>

                            {/* Основные преимущества в горизонтальном ряду */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-around',
                                marginBottom: '48px',
                                gap: '20px',
                                flexWrap: 'wrap'
                            }}>
                                <div style={{
                                    textAlign: 'center',
                                    flex: '1 1 200px',
                                    minWidth: '200px'
                                }}>
                                    <div style={{
                                        width: '100px',
                                        height: '100px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 16px'
                                    }}>
                                        <img src="/assets/banners/rakeback1.png" alt="Rakeback" style={{ width: '70px', height: '70px', objectFit: 'contain' }} />
                                    </div>
                                    <h3 style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '700', marginBottom: '8px' }}>
                                        МГНОВЕННО
                                    </h3>
                                    <p style={{ color: '#fff', fontSize: '0.9rem', margin: 0 }}>
                                        Без задержек и ожидания
                                    </p>
                                </div>

                                <div style={{
                                    textAlign: 'center',
                                    flex: '1 1 200px',
                                    minWidth: '200px'
                                }}>
                                    <div style={{
                                        width: '100px',
                                        height: '100px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 16px'
                                    }}>
                                        <img src="/assets/banners/rakeback2.png" alt="Без отыгрыша" style={{ width: '70px', height: '70px', objectFit: 'contain' }} />
                                    </div>
                                    <h3 style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '700', marginBottom: '8px' }}>
                                        БЕЗ ОТЫГРЫША
                                    </h3>
                                    <p style={{ color: '#fff', fontSize: '0.9rem', margin: 0 }}>
                                        Сразу выводите деньги
                                    </p>
                                </div>

                                <div style={{
                                    textAlign: 'center',
                                    flex: '1 1 200px',
                                    minWidth: '200px'
                                }}>
                                    <div style={{
                                        width: '100px',
                                        height: '100px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 16px'
                                    }}>
                                        <img src="/assets/banners/rakeback3.png" alt="Автоматически" style={{ width: '70px', height: '70px', objectFit: 'contain' }} />
                                    </div>
                                    <h3 style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '700', marginBottom: '8px' }}>
                                        АВТОМАТИЧЕСКИ
                                    </h3>
                                    <p style={{ color: '#fff', fontSize: '0.9rem', margin: 0 }}>
                                        Без заявок и ожидания
                                    </p>
                                </div>
                            </div>

                            

                            {/* Таймлайн процесса */}
                            <div style={{
                                marginBottom: '48px'
                            }}>
                                <h2 style={{
                                    fontSize: '2rem',
                                    fontWeight: '700',
                                    textAlign: 'center',
                                    marginBottom: '40px',
                                    color: '#fff',
                                    textTransform: 'uppercase',
                                    letterSpacing: '1px'
                                }}>
                                    🕐 Процесс получения Rakeback
                                </h2>

                                <div style={{ position: 'relative', maxWidth: '800px', margin: '0 auto' }}>
                                    {/* Вертикальная линия */}
                                    <div style={{
                                        position: 'absolute',
                                        left: '50%',
                                        top: '0',
                                        bottom: '0',
                                        width: '4px',
                                        background: 'linear-gradient(180deg, #ffc107 0%, #ff9800 100%)',
                                        transform: 'translateX(-50%)',
                                        borderRadius: '2px'
                                    }}></div>

                                    {/* Шаги таймлайна */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '40px', position: 'relative' }}>
                                        {/* Шаг 1 */}
                                        <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                                            <div style={{
                                                position: 'absolute',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: '100px',
                                                height: '100px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                zIndex: 2
                                            }}>
                                                <img src="/assets/banners/rakeback6.png" alt="Играйте" style={{ width: '50px', height: '70px', objectFit: 'contain' }} />
                                            </div>
                                            <div style={{
                                                width: 'calc(50% - 40px)',
                                                background: 'rgba(255, 87, 34, 0.1)',
                                                borderRadius: '16px',
                                                padding: '16px',
                                                border: '2px solid rgba(255, 87, 34, 0.3)',
                                                marginRight: 'auto',
                                                maxWidth: '300px',
                                                wordWrap: 'break-word',
                                                overflowWrap: 'break-word'
                                            }}>
                                                <h3 style={{
                                                    color: '#fff',
                                                    fontSize: '1.1rem',
                                                    fontWeight: '700',
                                                    marginBottom: '8px',
                                                    lineHeight: '1.3',
                                                    wordWrap: 'break-word'
                                                }}>
                                                    Играйте и делайте ставки
                                                </h3>
                                                <p style={{
                                                    color: '#fff',
                                                    fontSize: '0.9rem',
                                                    margin: 0,
                                                    lineHeight: '1.4',
                                                    wordWrap: 'break-word'
                                                }}>
                                                    Система автоматически отслеживает все ваши ставки и выигрыши
                                                </p>
                                            </div>
                                        </div>

                                        {/* Шаг 2 */}
                                        <div style={{ display: 'flex', alignItems: 'center', position: 'relative', justifyContent: 'flex-end' }}>
                                            <div style={{
                                                position: 'absolute',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: '100px',
                                                height: '100px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                zIndex: 2
                                            }}>
                                                <img src="/assets/banners/rakeback7.png" alt="Ждите 24 часа" style={{ width: '50px', height: '70px', objectFit: 'contain' }} />
                                            </div>
                                            <div style={{
                                                width: 'calc(50% - 40px)',
                                                background: 'rgba(33, 150, 243, 0.1)',
                                                borderRadius: '16px',
                                                padding: '16px',
                                                border: '2px solid rgba(33, 150, 243, 0.3)',
                                                marginLeft: 'auto',
                                                maxWidth: '300px',
                                                wordWrap: 'break-word',
                                                overflowWrap: 'break-word'
                                            }}>
                                                <h3 style={{
                                                    color: '#fff',
                                                    fontSize: '1.1rem',
                                                    fontWeight: '700',
                                                    marginBottom: '8px',
                                                    lineHeight: '1.3',
                                                    wordWrap: 'break-word'
                                                }}>
                                                    Ждите 24 часа
                                                </h3>
                                                <p style={{
                                                    color: '#fff',
                                                    fontSize: '0.9rem',
                                                    margin: 0,
                                                    lineHeight: '1.4',
                                                    wordWrap: 'break-word'
                                                }}>
                                                    Rakeback рассчитывается за полные сутки игровой активности
                                                </p>
                                            </div>
                                        </div>

                                        {/* Шаг 3 */}
                                        <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                                            <div style={{
                                                position: 'absolute',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: '100px',
                                                height: '100px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                zIndex: 2
                                            }}>
                                                <img src="/assets/banners/rakeback8.png" alt="Получите Rakeback" style={{ width: '50px', height: '70px', objectFit: 'contain' }} />
                                            </div>
                                            <div style={{
                                                width: 'calc(50% - 40px)',
                                                background: 'rgba(76, 175, 80, 0.1)',
                                                borderRadius: '16px',
                                                padding: '16px',
                                                border: '2px solid rgba(76, 175, 80, 0.3)',
                                                marginRight: 'auto',
                                                maxWidth: '300px',
                                                wordWrap: 'break-word',
                                                overflowWrap: 'break-word'
                                            }}>
                                                <h3 style={{
                                                    color: '#fff',
                                                    fontSize: '1.1rem',
                                                    fontWeight: '700',
                                                    marginBottom: '8px',
                                                    lineHeight: '1.3',
                                                    wordWrap: 'break-word'
                                                }}>
                                                    Получите Rakeback
                                                </h3>
                                                <p style={{
                                                    color: '#fff',
                                                    fontSize: '0.9rem',
                                                    margin: 0,
                                                    lineHeight: '1.4',
                                                    wordWrap: 'break-word'
                                                }}>
                                                    Деньги мгновенно поступают на основной счёт без отыгрыша
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Уникальная секция со статистикой и фактами */}
                            <div style={{
                                background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.05) 0%, rgba(255, 152, 0, 0.05) 100%)',
                                borderRadius: '24px',
                                padding: '40px',
                                border: '2px solid rgba(255, 193, 7, 0.2)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: '-100px',
                                    left: '-100px',
                                    width: '200px',
                                    height: '200px',
                                    background: 'radial-gradient(circle, rgba(255, 193, 7, 0.1) 0%, transparent 70%)',
                                    borderRadius: '50%'
                                }}></div>

                                <div style={{ position: 'relative', zIndex: 2 }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '12px',
                                        marginBottom: '32px'
                                    }}>
                                        <img src="/assets/banners/rakeback9.png" alt="В цифрах" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
                                        <h2 style={{
                                            fontSize: '2.2rem',
                                            fontWeight: '800',
                                            color: '#fff',
                                            textTransform: 'uppercase',
                                            letterSpacing: '2px',
                                            margin: 0
                                        }}>
                                            Rakeback в цифрах
                                        </h2>
                                    </div>

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                        gap: '24px',
                                        marginBottom: '32px'
                                    }}>
                                        <div style={{
                                            textAlign: 'center',
                                            padding: '24px',
                                            background: 'linear-gradient(135deg, rgba(255, 87, 34, 0.1) 0%, rgba(211, 47, 47, 0.1) 100%)',
                                            borderRadius: '20px',
                                            border: '2px solid rgba(255, 87, 34, 0.3)'
                                        }}>
                                            <div style={{
                                                fontSize: '3rem',
                                                fontWeight: '900',
                                                color: '#ff5722',
                                                marginBottom: '8px',
                                                lineHeight: '1'
                                            }}>
                                                24
                                            </div>
                                            <div style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '600', marginBottom: '4px' }}>
                                                ЧАСА
                                            </div>
                                            <div style={{ color: '#fff', fontSize: '0.9rem' }}>
                                                период расчёта
                                            </div>
                                        </div>

                                        <div style={{
                                            textAlign: 'center',
                                            padding: '24px',
                                            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(46, 125, 50, 0.1) 100%)',
                                            borderRadius: '20px',
                                            border: '2px solid rgba(76, 175, 80, 0.3)'
                                        }}>
                                            <div style={{
                                                fontSize: '3rem',
                                                fontWeight: '900',
                                                color: '#4CAF50',
                                                marginBottom: '8px',
                                                lineHeight: '1'
                                            }}>
                                                0
                                            </div>
                                            <div style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '600', marginBottom: '4px' }}>
                                                ОТЫГРЫША
                                            </div>
                                            <div style={{ color: '#fff', fontSize: '0.9rem' }}>
                                                сразу выводите
                                            </div>
                                        </div>

                                        <div style={{
                                            textAlign: 'center',
                                            padding: '24px',
                                            background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(106, 27, 154, 0.1) 100%)',
                                            borderRadius: '20px',
                                            border: '2px solid rgba(156, 39, 176, 0.3)'
                                        }}>
                                            <div style={{
                                                fontSize: '3rem',
                                                fontWeight: '900',
                                                color: '#9C27B0',
                                                marginBottom: '8px',
                                                lineHeight: '1'
                                            }}>
                                                ∞
                                            </div>
                                            <div style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '600', marginBottom: '4px' }}>
                                                ЛИМИТ
                                            </div>
                                            <div style={{ color: '#fff', fontSize: '0.9rem' }}>
                                                без ограничений
                                            </div>
                                        </div>
                                    </div>

                                    {/* Быстрые факты */}
                                    <div style={{
                                        background: 'rgba(0, 0, 0, 0.3)',
                                        borderRadius: '16px',
                                        padding: '24px'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            marginBottom: '16px'
                                        }}>
                                            <img src="/assets/banners/rakeback10.png" alt="Быстрые факты" style={{ width: '36px', height: '36px', objectFit: 'contain' }} />
                                            <h3 style={{
                                                color: '#fff',
                                                fontSize: '1.4rem',
                                                fontWeight: '700',
                                                textAlign: 'center',
                                                margin: 0
                                            }}>
                                                Быстрые факты
                                            </h3>
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '16px',
                                            maxWidth: '600px',
                                            margin: '0 auto'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '16px',
                                                padding: '16px',
                                                borderRadius: '12px',
                                                textAlign: 'left'
                                            }}>
                                                <div style={{
                                                    width: '60px',
                                                    height: '60px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    marginTop: '4px'
                                                }}>
                                                    <img src="/assets/banners/rakeback1.png" alt="Мгновенное зачисление" style={{ width: '70px', height: '70px', objectFit: 'contain' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '700', lineHeight: '1.3', marginBottom: '6px' }}>
                                                        Мгновенное зачисление
                                                    </div>
                                                    <div style={{ color: '#fff', fontSize: '0.95rem', opacity: '0.8', lineHeight: '1.3' }}>
                                                        без задержек
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '16px',
                                                padding: '16px',
                                                borderRadius: '12px',
                                                textAlign: 'left'
                                            }}>
                                                <div style={{
                                                    width: '60px',
                                                    height: '60px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    marginTop: '4px'
                                                }}>
                                                    <img src="/assets/banners/rakeback13.png" alt="Честный расчёт" style={{ width: '70px', height: '70px', objectFit: 'contain' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '700', lineHeight: '1.3', marginBottom: '6px' }}>
                                                        Честный расчёт
                                                    </div>
                                                    <div style={{ color: '#fff', fontSize: '0.95rem', opacity: '0.8', lineHeight: '1.3' }}>
                                                        прозрачная система
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '16px',
                                                padding: '16px',
                                                borderRadius: '12px',
                                                textAlign: 'left'
                                            }}>
                                                <div style={{
                                                    width: '60px',
                                                    height: '60px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    marginTop: '4px'
                                                }}>
                                                    <img src="/assets/banners/rakeback3.png" alt="Каждые 24 часа" style={{ width: '70px', height: '70px', objectFit: 'contain' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '700', lineHeight: '1.3', marginBottom: '6px' }}>
                                                        Каждые 24 часа
                                                    </div>
                                                    <div style={{ color: '#fff', fontSize: '0.95rem', opacity: '0.8', lineHeight: '1.3' }}>
                                                        ежедневно
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '16px',
                                                padding: '16px',
                                                borderRadius: '12px',
                                                textAlign: 'left'
                                            }}>
                                                <div style={{
                                                    width: '60px',
                                                    height: '60px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    marginTop: '4px'
                                                }}>
                                                    <img src="/assets/banners/rakeback12.png" alt="Персональный" style={{ width: '70px', height: '70px', objectFit: 'contain' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ color: '#fff', fontSize: '0.95rem', fontWeight: '700', lineHeight: '1.2', marginBottom: '6px' }}>
                                                        Персональный подход
                                                    </div>
                                                    <div style={{ color: '#fff', fontSize: '0.85rem', opacity: '0.8', lineHeight: '1.3' }}>
                                                        для каждого игрока
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showPaymentModal && (
                <PaymentModal onClose={() => setShowPaymentModal(false)} />
            )}
            {showAuthModal && (
                <AuthModal onClose={() => setShowAuthModal(false)} isLoginType={false} />
            )}
        </div>
    );
};

export default FreeMoney;
